import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useHover, useActive, useFocus } from '../../helpers/custom-hooks';

function getStateStyles(
  buttonStateStyles,
  isHovered,
  isActive,
  isFocused,
  isDisabled
) {
  if (isEmpty(buttonStateStyles)) return {};
  return ['hover', 'active', 'focus', 'disabled'].reduce((styles, state) => {
    if (state === 'hover' && isHovered) {
      return buttonStateStyles.hover;
    }
    if (state === 'active' && isActive) {
      return buttonStateStyles.active;
    }
    if (state === 'focus' && isFocused) {
      return buttonStateStyles.focus;
    }
    if (state === 'disabled' && isDisabled) {
      return buttonStateStyles.disabled;
    }

    return isEmpty(styles) ? buttonStateStyles.normal || {} : styles;
  }, {});
}

const BaseButton = ({
  isPrimary,
  isSecondary,
  isDisabled,
  label,
  onClick,
  buttonStateStyles,
  className,
  ...props
}) => {
  const [hoverRef, isHovered] = useHover();
  const [activeRef, isActive] = useActive();
  const [focusRef, isFocused] = useFocus();
  const attachRef = (el) => {
    hoverRef.current = el;
    activeRef.current = el;
    focusRef.current = el;
  };
  const stateStyles = getStateStyles(
    buttonStateStyles,
    isHovered,
    isActive,
    isFocused,
    isDisabled
  );
  const defaultClassName = isSecondary
    ? 'button-secondary'
    : isPrimary
    ? 'button-primary'
    : '';

  return (
    <button
      ref={attachRef}
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      className={[defaultClassName, className].join(' ')}
      style={stateStyles}
      {...props}
    >
      {label}
    </button>
  );
};

BaseButton.defaultProps = {
  isPrimary: true,
  isSecondary: false,
  isDisabled: false,
  buttonStateStyles: {},
  className: '',
};

BaseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  isPrimary: PropTypes.bool,
  isSecondary: PropTypes.bool,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  buttonStateStyles: PropTypes.object,
};

export default BaseButton;
